import React, { ReactNode, useMemo } from 'react';
import { batch } from 'react-redux';
import styled from 'styled-components';
import {
  IRowMeta,
  removeErrorAction,
  removeEventRowAction,
} from '../../../../../redux/slices/oldBuilder';
import {
  HighlightedEntity,
  useHighlightingContext,
  withHighlightingContext,
} from '../../../../HighlightingContext';
import { useActionsVisibilityContext } from '../../../../ActionsVisibilityContext';
import * as BuilderUI from '../../../BuilderUI';
import BuilderEventsInput from '../../BuilderEventsInput';
import {
  FieldContext,
  IEventInput,
  IFilterFieldsFragment,
  Maybe,
  TagOption,
} from '../../../../../utils/apollo/graphqlSchema';
import BuilderProperty from '../../BuilderProperty';
import {
  BuilderFilterFieldsHighlighted,
  BuilderFilterFieldsTrigger,
} from '../../BuilderFilterFields';
import { BuilderRowActions } from '../../../BuilderActions';
import { ITooltipOption } from '../../../../KubitTooltip/globalTooltips';
import useVirtualEventDataFromSearchParams from '../utils/useVirtualEventDataFromSearchParams';
import useNamedFilterDataFromSearchParams from '../utils/useNamedFilterDataFromSearchParams';
import { IStyled } from '../../../../../styles/types';
import { useAppDispatch, useAppSelector } from '../../../../../redux/hooks';
import DndDroppableZone from '../../../../DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../../../QuickAccessDictionary/DictionaryTabs/types';
import { eventConditionsPrimitiveSelector } from '../../../../../redux/selectors/builder';
import { isNil } from 'lodash';
import { useDndEntityInternalContext } from '../../../../DndEntity';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../../../DndEntity/utils';
import {
  EntityDropzoneDataTypeEnum,
  IFieldEntityDropzoneDataType,
  INamedFilterEntityDropzoneDataType,
} from '../../../../DndEntity/validation';
import DropzoneArea from '../../../../DndEntity/DndDroppableZone/DropzoneArea';
import { createTagOptionInput } from '../../../EntitySelection/utils';
import { defaultEventInput } from '../../../../../utils/builder';
import FieldDroppableZone from '../../../../DndEntity/DndDroppableZone/FieldDroppableZone';
import { SquareRemoveButton } from '../../../../Button';

interface IProps extends IStyled {
  rowMeta: IRowMeta;
  label?: ReactNode;
  placeholder?: string;
  eventsInput?: IEventInput[];
  actions?: ReactNode;
  filter?: Maybe<IFilterFieldsFragment>;
  onRemove?: () => void;
  removeActionTooltip?: ITooltipOption;
  canRenderRemove?: boolean;
  canRenderProperty?: boolean;
  canRenderFilters?: boolean;
}

const Container = styled(HighlightedEntity)`
  position: relative;
  display: flex;
  align-items: flex-start;
  column-gap: 8px;
  padding-top: 4px;
  padding-bottom: 4px;
  padding-left: 4px;
  border-radius: 4px;
`;

const Content = styled.div`
  flex: 1;
`;

const MainPart = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  column-gap: 16px;
  row-gap: 8px;
`;

const Label = styled(BuilderUI.InputLabel)`
  margin-top: 5px;
  padding-left: 4px;
`;

const defaultRemoveActionTooltip: ITooltipOption = {
  content: 'Remove Event(s).',
};

const EventCondition = ({
  rowMeta,
  label,
  placeholder,
  className,
  eventsInput,
  actions,
  filter,
  onRemove,
  removeActionTooltip,
  canRenderRemove,
  canRenderProperty,
  canRenderFilters,
}: IProps) => {
  const dispatch = useAppDispatch();

  const eventConditionSelector = useMemo(eventConditionsPrimitiveSelector, []);
  const eventConditions = useAppSelector((state) =>
    eventConditionSelector(state, rowMeta),
  );

  // check search params for virtual event data and populate
  useVirtualEventDataFromSearchParams(rowMeta);

  // check search params for named filter data and populate
  useNamedFilterDataFromSearchParams(rowMeta);

  const { activateHighlighting, deactivateHighlighting } =
    useHighlightingContext();

  const { actionsVisible, hideActions } = useActionsVisibilityContext();
  const { activeItem } = useDndEntityInternalContext();

  const handleRemove = () => {
    // if provided, use custom onRemove
    if (onRemove) {
      onRemove();
    } else {
      // otherwise use a default flow
      batch(() => {
        dispatch(removeErrorAction({ rowMeta, errorKey: '', clearRow: true }));
        dispatch(removeEventRowAction(rowMeta));
      });
    }

    deactivateHighlighting();
  };

  const showAddFieldDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (eventConditions?.length && !isNil(rowMeta.eventIndex)) {
      const currentEvent = eventConditions[rowMeta.eventIndex];

      if (!isNil(currentEvent.filter)) {
        return false;
      }
    }

    return data && data.__typename === 'TagLeafField';
  }, [activeItem, rowMeta, eventConditions]);

  const showAddFilterDropzone = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (eventConditions?.length && !isNil(rowMeta.eventIndex)) {
      const currentEvent = eventConditions[rowMeta.eventIndex];

      if (!isNil(currentEvent.filter)) {
        return false;
      }
    }

    return data && data.__typename === 'TagLeafFilter';
  }, [activeItem, rowMeta, eventConditions]);

  const fieldsDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Fields,
    rowMeta,
  );

  const filtersDropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Filters,
    rowMeta,
  );

  const tags = [
    createTagOptionInput(TagOption.Field, {
      field: {
        func: null,
        context: FieldContext.Filter,
        events: eventsInput ?? [defaultEventInput],
        arrayObjectContext: null,
      },
    }),
  ];

  return (
    <Container className={className}>
      {label && <Label>{label}</Label>}
      <Content>
        <MainPart>
          <BuilderEventsInput
            rowMeta={rowMeta}
            placeholder={placeholder}
            onOpen={hideActions}
          />

          {canRenderProperty && (
            <BuilderProperty rowMeta={rowMeta} eventsInput={eventsInput} />
          )}
          {canRenderFilters && (
            <>
              {showAddFieldDropzone ? (
                <FieldDroppableZone
                  dropzoneId={fieldsDropzoneId}
                  tags={tags}
                  data={
                    {
                      id: fieldsDropzoneId,
                      type: EntityDropzoneDataTypeEnum.Fields,
                      rowMeta,
                    } satisfies IFieldEntityDropzoneDataType
                  }
                  hideOnDisabled
                >
                  <DropzoneArea size="small">+ Add Event Filter</DropzoneArea>
                </FieldDroppableZone>
              ) : showAddFilterDropzone ? (
                <DndDroppableZone
                  accepts={[EntityDropzoneType.Filters]}
                  id={filtersDropzoneId}
                  data={
                    {
                      id: filtersDropzoneId,
                      type: EntityDropzoneDataTypeEnum.Filters,
                      rowMeta,
                    } satisfies INamedFilterEntityDropzoneDataType
                  }
                >
                  <DropzoneArea size="small">+ Add Event Filter</DropzoneArea>
                </DndDroppableZone>
              ) : (
                <BuilderFilterFieldsTrigger
                  rowMeta={rowMeta}
                  eventsInput={eventsInput}
                  fieldsDropzoneId={fieldsDropzoneId}
                />
              )}
            </>
          )}

          {actionsVisible && (
            <BuilderRowActions>
              {actions}
              {canRenderRemove && (
                <SquareRemoveButton
                  {...(removeActionTooltip || defaultRemoveActionTooltip)}
                  onMouseEnter={activateHighlighting}
                  onMouseLeave={deactivateHighlighting}
                  onClick={handleRemove}
                />
              )}
            </BuilderRowActions>
          )}
        </MainPart>
        {filter && filter?.conditions?.length > 0 && (
          <BuilderFilterFieldsHighlighted
            rowMeta={rowMeta}
            filter={filter}
            eventsInput={eventsInput}
          />
        )}
      </Content>
    </Container>
  );
};

export default withHighlightingContext(EventCondition);
